import type { TFunction } from "i18next";
import { isApiError } from "../../api/client.js";

const INVITE_LIMIT_KEY = "team.invite.limitReached";
const RENEW_LIMIT_KEY = "team.invite.renewLimitReached";

export type TeamInviteLimitFeedback =
  | { kind: "partial"; remaining: number; excess: number }
  | { kind: "none" }
  | { kind: "generic"; message: string };

function applicationErrorCode(error: unknown): string | undefined {
  if (isApiError(error)) {
    return error.status === 0 ? undefined : error.code;
  }
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" && code.length > 0 ? code : undefined;
  }
  return undefined;
}

export function isTeamMemberLimitReachedError(error: unknown): boolean {
  return applicationErrorCode(error) === "TEAM_MEMBER_LIMIT_REACHED";
}

/**
 * Localized invite-dialog copy for a team-seat rejection.
 * Returns null for every other error so the caller can keep its own fallback.
 * Never returns the backend English message.
 */
export function teamInviteLimitMessage(t: TFunction, error: unknown): string | null {
  if (!isTeamMemberLimitReachedError(error)) return null;
  return t(INVITE_LIMIT_KEY);
}

function parseOccupiedSeatNumbers(error: unknown): { limit: number; usage: number } | null {
  if (!isApiError(error) || error.details == null) return null;
  const limit = Number(error.details.limit);
  const usage = Number(error.details.usage);
  if (!Number.isInteger(limit) || !Number.isInteger(usage) || limit < 0 || usage < 0) {
    return null;
  }
  return { limit, usage };
}

/**
 * Splits a seat-limit rejection into partial capacity, zero seats, or the
 * generic string when `limit` / `usage` cannot be read. `attempted` is the
 * staged batch the dialog just submitted. Seat math stays on the server.
 */
export function teamInviteLimitFeedback(
  t: TFunction,
  error: unknown,
  attempted: number,
): TeamInviteLimitFeedback | null {
  if (!isTeamMemberLimitReachedError(error)) return null;
  const seats = parseOccupiedSeatNumbers(error);
  if (!seats || !Number.isInteger(attempted) || attempted < 1) {
    return { kind: "generic", message: t(INVITE_LIMIT_KEY) };
  }
  const remaining = seats.limit - seats.usage;
  if (remaining <= 0) return { kind: "none" };
  const excess = attempted - remaining;
  if (excess <= 0) return { kind: "generic", message: t(INVITE_LIMIT_KEY) };
  return { kind: "partial", remaining, excess };
}

/** Localized toast copy when an expired invitation cannot be renewed. */
export function teamRenewLimitMessage(t: TFunction, error: unknown): string | null {
  if (!isTeamMemberLimitReachedError(error)) return null;
  return t(RENEW_LIMIT_KEY);
}

/** Localized copy when the organization has no active OWNER to invite as. */
export function teamNoActiveOwnerMessage(t: TFunction, error: unknown): string | null {
  if (applicationErrorCode(error) !== "ORGANIZATION_HAS_NO_ACTIVE_OWNER") return null;
  return t("organization.platformAdmin.noActiveOwner");
}

/**
 * Form-level copy after a create-invitation rejection.
 * `active_member` is already explained on the row, so it returns null.
 * Every other case uses localized send copy and never the API message.
 */
export function invitationSubmitFormError(
  t: TFunction,
  rowCodes: readonly string[],
): string | null {
  if (rowCodes.length > 0 && rowCodes.every((code) => code === "active_member")) {
    return null;
  }
  return t("team.invite.sendError");
}
